import cloneDeep from 'lodash.clonedeep';
import * as immutable from 'object-path-immutable';

import { AvatarInfo, BlockedUserInfo, FriendGroupInfo } from "api";
import { WithContext } from "state/action-types";
import { ClientAction } from "state/action";
import { HomeState } from "state/home/state";
import { toWsUrl } from "util/url";

const emptyConnection: Omit<HomeState, "roots"> = {
    introduced: false,
    connecting: false,
    anonymousFullName: null,
    root: {
        location: null,
        page: null,
        api: null,
        events: null
    },
    login: null,
    owner: {
        name: null,
        verified: false,
        correct: false,
        changing: false
    },
    avatars: {
        loading: false,
        loaded: false,
        avatars: []
    },
    friendGroups: [],
    invisibleUsers: {
        checksum: 0,
        blockedUsers: []
    },
    features: null,
    connectionsDialog: {
        show: false
    }
};

const initialState: HomeState = {
    ...cloneDeep(emptyConnection),
    roots: []
};

function updateBlocked(state: HomeState, list: BlockedUserInfo[], append: boolean): HomeState {
    const blockedUsers = list.filter(bu =>
        bu.entryId == null && bu.entryNodeName == null && bu.entryPostingId == null
        && bu.blockedOperation === "visibility");
    if (blockedUsers.length === 0) {
        return state;
    }
    const ids = blockedUsers.map(bu => bu.id);
    return immutable.update(state, "invisibleUsers.blockedUsers",
        (bus: BlockedUserInfo[]) => bus.filter(bu => !ids.includes(bu.id)).concat(append ? blockedUsers : []));
}

export default (state: HomeState = initialState, action: WithContext<ClientAction>): HomeState => {
    switch (action.type) {
        case "CONNECT_TO_HOME":
            return {
                ...state,
                connecting: true
            };

        case "CONNECTION_TO_HOME_FAILED":
            return {
                ...state,
                connecting: false
            };

        case "CONNECTED_TO_HOME": {
            const {location, login, name, roots} = action.payload;
            let root = location.toLowerCase();
            return {
                ...state,
                introduced: false,
                connecting: false,
                root: {
                    location: root,
                    page: root + "/moera",
                    api: root + "/moera/api",
                    events: toWsUrl(root + "/moera/api/events"),
                },
                login,
                owner: {
                    ...state.owner,
                    name: name ?? null,
                    verified: false,
                    correct: false
                },
                roots: roots ?? state.roots
            };
        }

        case "DISCONNECTED_FROM_HOME":
            return {
                ...state,
                ...cloneDeep(emptyConnection)
            };

        case "HOME_READY":
            return {
                ...state,
                introduced: true
            };

        case "HOME_OWNER_SET":
            return {
                ...state,
                owner: {
                    ...state.owner,
                    name: action.payload.name,
                    verified: false,
                    correct: false,
                    changing: action.payload.changing ?? state.owner.changing
                }
            };

        case "HOME_OWNER_VERIFIED":
            if (state.owner.name === action.payload.name) {
                return {
                    ...state,
                    owner: {
                        ...state.owner,
                        name: action.payload.name,
                        correct: action.payload.correct,
                        verified: true
                    }
                };
            }
            return state;

        case "ANONYMOUS_FULL_NAME_SET":
            return immutable.set(state, "anonymousFullName", action.payload.anonymousFullName);

        case "CONNECTIONS_SET":
            return {
                ...state,
                roots: action.payload.roots
            };

        case "HOME_AVATARS_LOAD":
            return immutable.set(state, "avatars.loading", true);

        case "HOME_AVATARS_LOADED":
            return immutable.assign(state, "avatars", {
                loading: false,
                loaded: true,
                avatars: action.payload.avatars
            });

        case "HOME_AVATARS_LOAD_FAILED":
            return immutable.set(state, "avatars.loading", false);

        case "HOME_FRIEND_GROUPS_LOADED":
            return immutable.set(state, "friendGroups",
                action.payload.friendGroups.sort((a, b) => a.createdAt - b.createdAt));

        case "HOME_INVISIBLE_USERS_LOADED":
            return immutable.assign(state, "invisibleUsers", {
                checksum: action.payload.checksum,
                blockedUsers: action.payload.blockedUsers
            });

        case "OPEN_CONNECTIONS_DIALOG":
            return immutable.set(state, "connectionsDialog.show", true);

        case "CLOSE_CONNECTIONS_DIALOG":
            return immutable.set(state, "connectionsDialog.show", false);

        case "BLOCKED_USERS_ADDED":
            return updateBlocked(state, action.payload.blockedUsers, true);

        case "BLOCKED_USERS_DELETED":
            return updateBlocked(state, action.payload.blockedUsers, false);

        case "FRIEND_GROUP_ADDED":
            if (action.payload.nodeName === action.context.homeOwnerNameOrUrl) {
                return immutable.update(
                    state,
                    "friendGroups",
                    (fgs: FriendGroupInfo[]) =>
                        [...fgs.filter(fg => fg.id !== action.payload.details.id), action.payload.details]
                            .sort((a, b) => a.createdAt - b.createdAt)
                );
            }
            return state;

        case "PROFILE_AVATAR_CREATED":
            if (state.avatars.loaded) {
                return immutable.insert(state, "avatars.avatars", action.payload.avatar, 0);
            }
            return state;

        case "PROFILE_AVATAR_DELETED":
            if (state.avatars.loaded) {
                const avatars = state.avatars.avatars.filter(av => av.id !== action.payload.id);
                return immutable.set(state, "avatars.avatars", avatars);
            }
            return state;

        case "EVENT_HOME_AVATAR_ADDED":
            if (state.avatars.loaded) {
                const avatars = state.avatars.avatars.filter(av => av.id !== action.payload.avatar.id);
                avatars.push(action.payload.avatar);
                avatars.sort((a, b) => b.ordinal - a.ordinal);
                return immutable.set(state, "avatars.avatars", avatars);
            }
            return state;

        case "EVENT_HOME_AVATAR_DELETED":
            if (state.avatars.loaded) {
                const avatars = state.avatars.avatars.filter(av => av.id !== action.payload.id);
                return immutable.set(state, "avatars.avatars", avatars);
            }
            return state;

        case "EVENT_HOME_AVATAR_ORDERED":
            if (state.avatars.loaded) {
                const avatars: AvatarInfo[] = [];
                state.avatars.avatars.forEach(av => {
                    if (av.id !== action.payload.id) {
                        avatars.push(av);
                    } else {
                        avatars.push({...av, ordinal: action.payload.ordinal});
                    }
                });
                avatars.sort((a, b) => b.ordinal - a.ordinal);
                return immutable.set(state, "avatars.avatars", avatars);
            }
            return state;

        case "EVENT_HOME_FRIEND_GROUP_ADDED":
        case "EVENT_HOME_FRIEND_GROUP_UPDATED":
            return immutable.update(
                state,
                "friendGroups",
                (fgs: FriendGroupInfo[]) =>
                    [...fgs.filter(fg => fg.id !== action.payload.friendGroup.id), action.payload.friendGroup]
                        .sort((a, b) => a.createdAt - b.createdAt)
            );

        case "EVENT_HOME_FRIEND_GROUP_DELETED":
            return immutable.update(
                state,
                "friendGroups",
                (fgs: FriendGroupInfo[]) => fgs.filter(fg => fg.id !== action.payload.friendGroupId)
            );

        case "NODE_FEATURES_LOADED":
            if (state.owner.name === action.payload.nodeName) {
                return {
                    ...state,
                    features: action.payload.features
                };
            }
            return state;

        default:
            return state;
    }
}
